/**
 * Station name search, run locally.
 *
 * The client already holds every station for the session (`/api/stations`), so
 * this needs no request and no debounce — matches appear on the keystroke,
 * ahead of the geocoder's results.
 */

import { MODE_BITS, modeShorts } from './palette';
import type { GeocodeResult, Stations } from './types';

/**
 * Fold the spellings apart from the ones people type: case, umlauts, the ß, and
 * the VBB's abbreviations. "S Oranienburger Str." and "oranienburger straße"
 * have to land on the same string.
 */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    // Suffix, not just a standalone word: "Friedrichstr." has to reach
    // "Friedrichstraße", and there is no word break in front of that "str".
    .replace(/str\b/g, 'strasse')
    .replace(/\bbhf\b/g, 'bahnhof')
    .replace(/\bpl\b/g, 'platz');
}

/**
 * Drop the network prefix VBB puts on rail stations: "S+U Alexanderplatz" is
 * typed as "Alexanderplatz", and the badges say S+U better than the text does.
 */
const PREFIX = /^(?:S\+U|U\+S|S|U)\s+/;
const PREFIX_NORM = /^(?:[su] )+/;

/**
 * What a name is matched on: normalised, minus the network prefix and minus a
 * leading "Berlin" — every stop here is in Berlin, and carrying it would rank
 * "Berlin Hauptbahnhof" below any bus stop that merely starts with the word.
 */
function stationKey(s: string): string {
  return normalize(s).replace(PREFIX_NORM, '').replace(/^berlin /, '');
}

/** Rail before bus when two stops match equally well. */
const MODE_RANK: Record<string, number> = { S: 0, U: 1, R: 2, T: 3, F: 4, B: 5 };

function rank(modes: number): number {
  let best = 9;
  for (const m of MODE_BITS) {
    if (modes & m.bit) best = Math.min(best, MODE_RANK[m.short] ?? 9);
  }
  return best;
}

/** Lower is better; -1 means no match. */
function score(name: string, q: string): number {
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  if (name.includes(` ${q}`)) return 2;
  if (name.includes(q)) return 3;
  // Last resort: every word of the query appears somewhere ("alex platz").
  const words = q.split(' ');
  return words.length > 1 && words.every((w) => name.includes(w)) ? 4 : -1;
}

/** Cheap metres-apart, good enough for de-duplicating against the geocoder. */
export function metresApart(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dy = (aLat - bLat) * 111_320;
  const dx = (aLon - bLon) * 111_320 * Math.cos((aLat * Math.PI) / 180);
  return Math.hypot(dx, dy);
}

// Normalised names are derived once per station list, not once per keystroke.
let cacheFor: Stations | null = null;
let cacheNames: string[] = [];

function normalizedNames(stations: Stations): string[] {
  if (cacheFor !== stations) {
    cacheFor = stations;
    cacheNames = stations.name.map(stationKey);
  }
  return cacheNames;
}

export function searchStations(stations: Stations, query: string, limit = 5): GeocodeResult[] {
  const q = stationKey(query);
  if (q.length < 2) return [];
  const names = normalizedNames(stations);

  const hits: { i: number; s: number }[] = [];
  for (let i = 0; i < names.length; i++) {
    const s = score(names[i], q);
    if (s >= 0) hits.push({ i, s });
  }

  hits.sort(
    (a, b) =>
      a.s - b.s ||
      rank(stations.modes[a.i]) - rank(stations.modes[b.i]) ||
      names[a.i].length - names[b.i].length
  );

  /*
   * One interchange is often several entries in the index — the U-Bahn platform
   * and the bus stop on the street above are separate nodes with the same name.
   * As search results that reads as a bug, so same-name stops within a block of
   * each other collapse to one row and pool their modes. The winning entry's
   * coordinates are kept, which is the rail platform wherever there is one.
   */
  const picked: { i: number; modes: number }[] = [];
  for (const { i } of hits.slice(0, 200)) {
    const same = picked.find(
      (p) =>
        names[p.i] === names[i] &&
        metresApart(stations.lat[p.i], stations.lon[p.i], stations.lat[i], stations.lon[i]) <= 500
    );
    if (same) {
      same.modes |= stations.modes[i];
    } else if (picked.length < limit) {
      picked.push({ i, modes: stations.modes[i] });
    }
  }

  return picked.map(({ i, modes }) => ({
    label: stations.name[i].replace(PREFIX, ''),
    detail: modeShorts(modes)
      .map((m) => m.name)
      .join(' · '),
    lat: stations.lat[i],
    lon: stations.lon[i],
    modes,
    kind: 'station',
    source: 'station',
  }));
}
